/**
 * Sky: gradient dome with square sun + moon, pixel stars and the sunrise /
 * sunset fan, plus the blocky cloud layer. Owns the day/night cycle and
 * writes the shared environment uniforms (light, ambient, sky, fog) each frame.
 *
 * All colours are LINEAR radiance (the scene renders to an HDR target).
 * Hex constants are authored in sRGB; THREE.Color converts them to linear.
 */

import * as THREE from 'three';
import { DAY_LENGTH, FAR_PLANE } from '../constants';
import { Clouds } from './clouds';
import type { EnvUniforms } from './env';

const SKY_VERT = /* glsl */ `
out vec3 vDir;

void main() {
  vDir = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SKY_FRAG = /* glsl */ `
precision highp float;

uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uFogColor;
uniform vec3 uSunGlow;
uniform vec3 uTrueSunDir;
uniform float uTime;

uniform vec3 uSunDisc;   // HDR radiance of the square sun
uniform vec3 uSunHalo;   // soft halo colour, already faded by elevation
uniform vec3 uMoonColor;
uniform float uStars;    // 0 = day, 1 = full night
uniform vec2 uStarRot;   // (cos, sin) of the orbit angle
uniform float uMurk;     // 0 above water, ~1 submerged

in vec3 vDir;

out vec4 fragColor;

const float SUN_SIZE = 0.085;  // half-width on the tangent plane
const float MOON_SIZE = 0.065;
const float STAR_GRID = 56.0;  // cells per unit on each cube face

float hash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

// Coordinates of dir in an orthonormal frame around body direction b:
// xy = tangent plane (x along the orbit), z = cosine to the body.
// The bodies orbit about +z, so cross(z, b) is never degenerate.
vec3 bodyFrame(vec3 dir, vec3 b) {
  vec3 right = normalize(vec3(-b.y, b.x, 0.0));
  vec3 up = cross(b, right);
  return vec3(dot(dir, right), dot(dir, up), dot(dir, b));
}

void main() {
  vec3 dir = normalize(vDir);
  float h = dir.y;

  // zero slope at the horizon: a flat band of pure horizon colour for the
  // fogged terrain to melt into; everything below it stays horizon/fog
  vec3 col = mix(uHorizon, uZenith, pow(smoothstep(0.0, 0.6, h), 0.75));

  // sunrise / sunset fan: hugs the horizon around the sun's azimuth
  vec2 sunAz = normalize(uTrueSunDir.xz);
  float az = max(dot(dir.xz / max(length(dir.xz), 1e-4), sunAz), 0.0);
  float az2 = az * az;
  col += uSunGlow * (az2 * az2 * exp(-abs(h) * 5.0) + 0.12 * exp(-abs(h) * 9.0));

  // stars: one square per sparse cell of a cube-face grid, turning with the orbit
  if (uStars > 0.002 && h > 0.0) {
    vec3 d = vec3(
      uStarRot.x * dir.x + uStarRot.y * dir.y,
      uStarRot.x * dir.y - uStarRot.y * dir.x,
      dir.z
    );
    vec3 a = abs(d);
    vec2 uv;
    float face;
    if (a.x >= a.y && a.x >= a.z) { uv = d.yz / a.x; face = d.x > 0.0 ? 0.0 : 1.0; }
    else if (a.y >= a.z) { uv = d.xz / a.y; face = d.y > 0.0 ? 2.0 : 3.0; }
    else { uv = d.xy / a.z; face = d.z > 0.0 ? 4.0 : 5.0; }
    vec2 g = uv * STAR_GRID;
    vec2 cell = floor(g) + face * 131.0;
    vec2 f = fract(g) - 0.5;
    if (hash21(cell) > 0.986) {
      float r = hash21(cell + 17.3);
      float star = step(max(abs(f.x), abs(f.y)), mix(0.10, 0.24, r));
      float twinkle = 0.78 + 0.22 * sin(uTime * (1.2 + r * 3.0) + r * 40.0);
      vec3 tint = mix(vec3(0.75, 0.85, 1.0), vec3(1.0, 0.92, 0.8), hash21(cell + 5.1));
      col += tint * (0.3 + 0.9 * r) * star * twinkle * uStars * smoothstep(0.02, 0.3, h);
    }
  }

  // bodies set into the horizon haze rather than shining through the void
  float above = smoothstep(-0.05, -0.005, h);

  // sun: crisp HDR square (bloom picks it up) + soft wide halo
  vec3 s = bodyFrame(dir, uTrueSunDir);
  if (s.z > 0.0) {
    vec2 p = s.xy / s.z;
    float core = step(max(abs(p.x), abs(p.y)), SUN_SIZE);
    float halo = 0.8 * pow(s.z, 256.0) + 0.22 * pow(s.z, 24.0);
    col += (uSunDisc * core + uSunHalo * halo) * above;
  }

  // moon: pale square, 8x8 "pixels" with a few darker crater cells
  vec3 m = bodyFrame(dir, -uTrueSunDir);
  if (m.z > 0.0) {
    vec2 p = m.xy / (m.z * MOON_SIZE);
    col += uMoonColor * 0.04 * pow(m.z, 64.0) * above;
    if (max(abs(p.x), abs(p.y)) < 1.0) {
      float c = hash21(floor((p * 0.5 + 0.5) * 8.0) + 7.0);
      float shade = c > 0.72 ? 0.6 : (c > 0.45 ? 0.85 : 1.0);
      col = mix(col, uMoonColor * shade, above);
    }
  }

  // submerged: the dome drowns in the water fog like everything else
  col = mix(col, uFogColor, uMurk);

  fragColor = vec4(col, 1.0);
}
`;

// --- design colours (sRGB hex -> linear) -----------------------------------
// a touch more saturated than vanilla plains (#78A7FF sky / #C0D8FF fog)
const DAY_ZENITH = new THREE.Color(0x78a7ff);
const DAY_HORIZON = new THREE.Color(0xc0d8ff);
const NIGHT_ZENITH = new THREE.Color(0x03050d);
const NIGHT_HORIZON = new THREE.Color(0x0a1224);
const SUNSET_ZENITH = new THREE.Color(0x3a4a8c);
const SUNSET_HORIZON = new THREE.Color(0xf2a07a);

// --- linear radiance constants ----------------------------------------------
const SUN_NOON = new THREE.Color(3.2, 3.0, 2.7);
const SUN_LOW = new THREE.Color(3.0, 1.3, 0.42);
const MOON_LIGHT = new THREE.Color(0.10, 0.14, 0.26);
const AMBIENT_DAY = new THREE.Color(0.74 * 0.9, 0.84 * 0.9, 1.0 * 0.9);
const AMBIENT_NIGHT = new THREE.Color(0.19, 0.23, 0.34); // ~0.3 of day: vanilla skyDarken floor before the Brightness curve
const AMBIENT_SUNSET = new THREE.Color(0.62, 0.52, 0.5);
const GLOW = new THREE.Color(1.0, 0.40, 0.10);
const SUN_DISC_NOON = new THREE.Color(34, 31, 25);
const SUN_DISC_LOW = new THREE.Color(38, 17, 5);
const SUN_HALO_NOON = new THREE.Color(1.0, 0.9, 0.7);
const SUN_HALO_LOW = new THREE.Color(1.0, 0.5, 0.18);
const MOON_DISC = new THREE.Color(1.9, 2.0, 2.3);
const WATER_FOG = new THREE.Color(0.015, 0.09, 0.16);
const CLOUD_DAY = new THREE.Color(1.1, 1.1, 1.1);
const CLOUD_NIGHT = new THREE.Color(0.07, 0.09, 0.15);
const CLOUD_SUNSET = new THREE.Color(1.0, 0.6, 0.42);

/** Shadow maps degenerate with a horizontal light; keep it at least this high. */
const MIN_LIGHT_Y = 0.08;

const _cloudTint = new THREE.Color();

export class Sky {
  /** Add to the scene; contains the dome and the clouds. Follows the camera. */
  readonly group = new THREE.Group();
  /** 0..1 through the day; 0.25 = noon, 0.75 = midnight. */
  time = 0.25;

  private readonly env: EnvUniforms;
  private readonly clouds = new Clouds();
  private readonly sunDisc = new THREE.Color();
  private readonly sunHalo = new THREE.Color();
  private readonly starRot = new THREE.Vector2(1, 0);
  private readonly stars: THREE.IUniform<number> = { value: 0 };
  private readonly murk: THREE.IUniform<number> = { value: 0 };
  /** Seconds spent underwater (vanilla's waterVisionTime, in seconds), for the fog ramp. */
  private underwaterTime = 0;
  /** Camera eye inside lava (set by the game before update): vanilla's dense red lava fog. */
  eyeInLava = false;

  constructor(env: EnvUniforms) {
    this.env = env;

    const material = new THREE.ShaderMaterial({
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      glslVersion: THREE.GLSL3,
      uniforms: {
        // shared: same uniform objects the terrain reads
        uZenith: env.uZenith,
        uHorizon: env.uHorizon,
        uFogColor: env.uFogColor,
        uSunGlow: env.uSunGlow,
        uTrueSunDir: env.uTrueSunDir,
        uTime: env.uTime,
        // dome only
        uSunDisc: { value: this.sunDisc },
        uSunHalo: { value: this.sunHalo },
        uMoonColor: { value: MOON_DISC },
        uStars: this.stars,
        uStarRot: { value: this.starRot },
        uMurk: this.murk,
      },
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(FAR_PLANE * 0.9, 32, 16), material);
    dome.frustumCulled = false;
    dome.renderOrder = -100;
    this.group.add(dome);

    this.group.add(this.clouds.mesh);
  }

  /** fogFar: world-unit distance at which terrain is fully fogged (render distance). */
  update(dt: number, camera: THREE.Camera, underwater: boolean, fogFar: number): void {
    const env = this.env;

    this.time = (this.time + dt / DAY_LENGTH) % 1;
    if (this.time < 0) this.time += 1;
    const angle = this.time * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const sun = env.uTrueSunDir.value.set(cos, sin, 0.25).normalize();
    const elevation = sun.y;

    const day = smoothstep(-0.14, 0.28, elevation);
    // bell around the horizon crossing, biased to when the sun is just up
    const sunset = 1 - smoothstep(0, 0.4, Math.abs(elevation - 0.04));
    // 1 while the sun is low: reddens direct light and the disc
    const low = 1 - smoothstep(0.04, 0.5, elevation);

    // Dominant light body. Both fade to black at elevation 0, which is where
    // the direction flips from sun to moon, so the swap is invisible.
    const light = env.uSunDir.value.copy(sun);
    if (elevation >= 0) {
      env.uSunColor.value.lerpColors(SUN_NOON, SUN_LOW, low).multiplyScalar(smoothstep(0, 0.14, elevation));
    } else {
      light.negate();
      env.uSunColor.value.copy(MOON_LIGHT).multiplyScalar(smoothstep(0, 0.14, -elevation));
    }
    if (light.y < MIN_LIGHT_Y) {
      const k = Math.sqrt((1 - MIN_LIGHT_Y * MIN_LIGHT_Y) / (light.x * light.x + light.z * light.z));
      light.set(light.x * k, MIN_LIGHT_Y, light.z * k);
    }

    env.uDayLight.value = day;
    env.uAmbientColor.value.lerpColors(AMBIENT_NIGHT, AMBIENT_DAY, day).lerp(AMBIENT_SUNSET, sunset * 0.5);

    env.uZenith.value.lerpColors(NIGHT_ZENITH, DAY_ZENITH, day).lerp(SUNSET_ZENITH, sunset * 0.45);
    // only mildly warm: this is also the fog colour all round the compass;
    // the strong orange is directional and lives in uSunGlow
    const horizon = env.uHorizon.value.lerpColors(NIGHT_HORIZON, DAY_HORIZON, day).lerp(SUNSET_HORIZON, sunset * 0.5);
    env.uSunGlow.value.copy(GLOW).multiplyScalar(sunset * 0.9);

    this.underwaterTime = underwater && !this.eyeInLava ? this.underwaterTime + dt : 0;
    if (this.eyeInLava) {
      // vanilla FogRenderer lava: (0.6, 0.1, 0.0), 0.25 .. 1 block
      env.uFogColor.value.setRGB(0.32, 0.01, 0.0);
      env.uFogNear.value = 0.25;
      env.uFogFar.value = 1.0;
    } else if (underwater) {
      env.uFogColor.value.copy(WATER_FOG).multiplyScalar(0.12 + 0.88 * day);
      // vanilla FogRenderer: linear from just behind the eye out to 96 blocks
      // times the water vision, which clears from 1/4 as you stay under
      env.uFogNear.value = 0;
      env.uFogFar.value = 96 * Math.max(0.25, waterVision(this.underwaterTime));
    } else {
      env.uFogColor.value.copy(horizon);
      env.uFogNear.value = fogFar * 0.72;
      env.uFogFar.value = fogFar;
    }

    // dome
    this.sunDisc.lerpColors(SUN_DISC_NOON, SUN_DISC_LOW, low);
    this.sunHalo.lerpColors(SUN_HALO_NOON, SUN_HALO_LOW, low).multiplyScalar(smoothstep(-0.12, 0.06, elevation));
    this.stars.value = 1 - smoothstep(-0.2, 0.05, elevation);
    this.starRot.set(cos, sin);
    this.murk.value = underwater ? 0.92 : 0;

    this.group.position.copy(camera.position);

    // clouds: white by day, pink-orange at sunset, dim blue at night
    _cloudTint.lerpColors(CLOUD_NIGHT, CLOUD_DAY, day).lerp(CLOUD_SUNSET, sunset * 0.7);
    this.clouds.update(dt, camera, _cloudTint, horizon);
    // Clouds.update leaves a world position; the group rides the camera
    this.clouds.mesh.position.sub(this.group.position);
    // from below the surface they would shine through the water fog
    this.clouds.mesh.visible = !underwater;
  }
}

/** LocalPlayer.getWaterVision with ticks as seconds: 60% clear over the first
 *  5 s under, the rest by 30 s. */
function waterVision(seconds: number): number {
  const t = seconds * 20;
  if (t >= 600) return 1;
  const a = Math.min(1, Math.max(0, t / 100));
  const b = t < 100 ? 0 : Math.min(1, Math.max(0, (t - 100) / 500));
  return a * a * 0.6 + b * 0.4;
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
